import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetKitchenSkillsQuery } from '../../../../../services/endpoints/content/kitchenSkills';
import { getCurrentUser } from '../../../../../services/helper';
import { Scopes } from '../../../../../services/endpoints/people/user';
import { kitchenSkill } from '../../../../../types/kitchenSkills.type';
import { kitchenSkillsColumns } from '../../../../../utils/consts/columnDefs/kitchenSkills';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import { canViewKitchenSkills } from '../../../../../utils/rbac';

const defaultScope = 'all';

export const KitchenSkillsView = () => {
  const { addToast } = useToast();
  const navigate: NavigateFunction = useNavigate();
  const currentUser = getCurrentUser();
  const userHasAccess = canViewKitchenSkills(currentUser);

  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching } = useGetKitchenSkillsQuery(filters, {
    skip: !userHasAccess
  });
  const [kitchenSkill, setKitchenSkill] = useState<kitchenSkill[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'description',
      label: 'Description',
      value: getFilterValue(filters.data?.filterBy, 'description')
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
            data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
            data.scopes.map((sMap) =>
                Object.entries(sMap).map(([scope, title]) => {
                  let scope_count = 0;
                  if (data.scope_counts != null) {
                    scope_count = data.scope_counts[scope] ?? 0;
                  }
                  return {
                    title: `${title} (${scope_count})`,
                    key: scope
                  };
                })
            )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setKitchenSkill(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  if (!userHasAccess) {
    return <Forbidden />;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={`${scopes[filters?.scope!] ?? ''} Kitchen Skills`}
            columns={kitchenSkillsColumns}
            data={kitchenSkill}
            count={data?.count}
            defaultScope={filters.scope ?? defaultScope}
            scopes={buttons}
            isLoading={isLoading}
            isFetching={isFetching}
            indexDownloadPath="kitchen-skills"
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
              />
            }
            onFiltersChanged={setFilters}
            newItemEl={
              <SimpleButton
                  variant="sfc-blueberry"
                  height={40}
                  truncate={false}
                  onClick={() => navigate('/admin/content/kitchen-skills/new')}
              >
                New Kitchen skill
              </SimpleButton>
            }
        />
      </div>
  );
};